import React, { useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { AlertTriangle, CheckCircle2, Download, Upload } from "lucide-react";

import Modal from "src/components/ui/Modal";
import Button from "src/components/ui/Button";
import TableV2 from "src/components/ui/TableV2";

export interface BulkImportColumn {
  /** Canonical field key — matches the key sent to `onImport` for each row. */
  key: string;
  /** Header text used both in the downloadable sample sheet and the preview table. */
  label: string;
  required?: boolean;
  /** Example value used to build the sample sheet's one demo row. */
  example?: string | number;
  /** Extra header spellings accepted from an uploaded sheet (case/space-insensitive). */
  aliases?: string[];
}

export interface BulkImportRowFailure {
  index: number;
  name?: string;
  error: string;
}

export interface BulkImportResult {
  imported: number;
  failed: BulkImportRowFailure[];
  total?: number;
  /**
   * Set only when rows can fold into fewer records than there are rows
   * (e.g. several variant rows becoming one product) — omit when each row
   * always maps 1:1 to one created record.
   */
  recordsCreated?: number;
}

interface BulkImportModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Plural noun used in copy, e.g. "products". */
  entityLabel: string;
  columns: BulkImportColumn[];
  sampleFileName: string;
  onImport: (rows: Array<Record<string, any>>) => Promise<BulkImportResult>;
  /** Called after a successful (even partial) import — typically refreshes the list behind the modal. */
  onImported?: () => void;
  maxPreviewRows?: number;
  /**
   * Full example rows for the sample sheet, keyed by column `key` — use
   * this instead of per-column `example` when the sheet needs more than
   * one demo row (e.g. showing the repeated-name convention for variants).
   * Falls back to a single row built from each column's `example` value.
   */
  sampleRows?: Array<Record<string, string | number>>;
  /** Extra guidance shown above the file picker, e.g. explaining a naming convention. */
  helperNote?: React.ReactNode;
  /**
   * Rows are sent to `onImport` in batches of this size, one request at a
   * time, instead of a single request for the whole file — keeps each
   * request well under any reverse-proxy timeout and gives real
   * incremental progress instead of an indefinite spinner. Default 100.
   */
  chunkSize?: number;
}

type Step = "select" | "preview" | "importing" | "result";

const normalizeHeader = (value: string) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, " ");

const buildHeaderMap = (columns: BulkImportColumn[]) => {
  const map = new Map<string, string>();
  columns.forEach((col) => {
    map.set(normalizeHeader(col.label), col.key);
    map.set(normalizeHeader(col.key), col.key);
    (col.aliases || []).forEach((alias) => map.set(normalizeHeader(alias), col.key));
  });
  return map;
};

const readFileAsArrayBuffer = (
  file: File,
  onProgress: (percent: number) => void
): Promise<ArrayBuffer> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    reader.onload = () => {
      onProgress(100);
      resolve(reader.result as ArrayBuffer);
    };
    reader.onerror = () => reject(reader.error || new Error("Failed to read file"));
    reader.readAsArrayBuffer(file);
  });

const BulkImportModal: React.FC<BulkImportModalProps> = ({
  open,
  onClose,
  title,
  entityLabel,
  columns,
  sampleFileName,
  onImport,
  onImported,
  maxPreviewRows = 200,
  sampleRows,
  helperNote,
  chunkSize = 100,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>("select");
  const [fileName, setFileName] = useState("");
  const [readProgress, setReadProgress] = useState(0);
  const [rows, setRows] = useState<Array<Record<string, any>>>([]);
  const [parseError, setParseError] = useState("");
  const [result, setResult] = useState<BulkImportResult | null>(null);
  const [importProgress, setImportProgress] = useState(0);

  const headerMap = useMemo(() => buildHeaderMap(columns), [columns]);

  const reset = () => {
    setStep("select");
    setFileName("");
    setReadProgress(0);
    setRows([]);
    setParseError("");
    setResult(null);
    setImportProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const downloadSample = () => {
    const sourceRows =
      sampleRows && sampleRows.length
        ? sampleRows
        : [
            columns.reduce((acc, col) => {
              acc[col.key] = col.example ?? "";
              return acc;
            }, {} as Record<string, string | number>),
          ];

    const sheetRows = sourceRows.map((row) => {
      const sheetRow: Record<string, any> = {};
      columns.forEach((col) => {
        sheetRow[col.label] = row[col.key] ?? "";
      });
      return sheetRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(sheetRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sample");
    XLSX.writeFile(workbook, sampleFileName);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setParseError("");
    setReadProgress(0);
    setStep("select");

    try {
      const buffer = await readFileAsArrayBuffer(file, setReadProgress);
      const workbook = XLSX.read(buffer, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      if (!firstSheetName) throw new Error("No sheet found in this file");
      const sheet = workbook.Sheets[firstSheetName];
      const rawRows = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, {
        defval: "",
      });

      if (!rawRows.length) {
        throw new Error("No rows found in this file");
      }

      const mappedRows = rawRows.map((rawRow) => {
        const mapped: Record<string, any> = {};
        Object.keys(rawRow).forEach((rawKey) => {
          const canonicalKey = headerMap.get(normalizeHeader(rawKey));
          if (canonicalKey) {
            const value = rawRow[rawKey];
            mapped[canonicalKey] = typeof value === "string" ? value.trim() : value;
          }
        });
        return mapped;
      });

      setRows(mappedRows);
      setStep("preview");
    } catch (err: any) {
      setParseError(err?.message || "Could not read this file");
    }
  };

  const missingRequiredCount = (row: Record<string, any>) =>
    columns.filter((col) => col.required && !row[col.key]).length;

  const invalidRowCount = rows.filter((row) => missingRequiredCount(row) > 0).length;

  const handleImport = async () => {
    setStep("importing");
    setImportProgress(0);

    // Sent in batches, one request at a time, instead of one giant
    // request — keeps each request well clear of any reverse-proxy
    // timeout and lets progress reflect rows actually processed so far
    // rather than a spinner with no real signal behind it.
    const chunks: Array<Array<Record<string, any>>> = [];
    for (let i = 0; i < rows.length; i += chunkSize) {
      chunks.push(rows.slice(i, i + chunkSize));
    }

    const merged: BulkImportResult = {
      imported: 0,
      failed: [],
      total: rows.length,
      recordsCreated: 0,
    };
    let recordsCreatedKnown = false;
    let offset = 0;

    for (const chunk of chunks) {
      try {
        const chunkResult = await onImport(chunk);
        merged.imported += chunkResult.imported;
        merged.failed.push(
          ...chunkResult.failed.map((f) => ({
            ...f,
            index: f.index >= 0 ? f.index + offset : f.index,
          }))
        );
        if (chunkResult.recordsCreated !== undefined) {
          recordsCreatedKnown = true;
        }
        merged.recordsCreated =
          (merged.recordsCreated || 0) +
          (chunkResult.recordsCreated ?? chunkResult.imported);
      } catch (err: any) {
        // The chunk never reached the server (network error, 5xx, etc.) —
        // mark every row in it as failed instead of silently dropping them.
        chunk.forEach((_, i) => {
          merged.failed.push({
            index: offset + i,
            error: err?.message || "Import failed",
          });
        });
      }
      offset += chunk.length;
      setImportProgress(Math.min(offset, rows.length));
    }

    if (!recordsCreatedKnown) merged.recordsCreated = undefined;

    setResult(merged);
    setStep("result");
    onImported?.();
  };

  const downloadFailures = () => {
    if (!result?.failed?.length) return;
    const failureRows = result.failed.map((f) => ({
      Row: f.index >= 0 ? f.index + 2 : "-", // +2: header row + 1-indexing
      Name: f.name || "",
      Error: f.error,
    }));
    const worksheet = XLSX.utils.json_to_sheet(failureRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Failed rows");
    XLSX.writeFile(workbook, `${sampleFileName.replace(/\.xlsx?$/i, "")}-failures.xlsx`);
  };

  const previewRows = rows.slice(0, maxPreviewRows);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      size={step === "preview" ? "full" : "lg"}
      bodyClassName="p-4 overflow-y-auto flex-1"
      footer={
        step === "preview" ? (
          <>
            <Button variant="outline" onClick={reset}>
              Discard
            </Button>
            <Button onClick={handleImport} disabled={rows.length === 0}>
              Import {rows.length} row{rows.length === 1 ? "" : "s"}
            </Button>
          </>
        ) : step === "result" ? (
          <>
            {!!result?.failed?.length && (
              <Button variant="outline" onClick={downloadFailures}>
                <Download size={16} className="mr-1.5" />
                Download failed rows ({result.failed.length})
              </Button>
            )}
            <Button onClick={handleClose}>Done</Button>
          </>
        ) : undefined
      }
    >
      {step === "select" && (
        <div className="space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Import {entityLabel} in bulk from an Excel or CSV file. Download the
            sample sheet to see the expected columns, fill it in, then upload it
            below.
          </p>

          {helperNote && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300">
              {helperNote}
            </div>
          )}

          <Button variant="outline" onClick={downloadSample}>
            <Download size={16} className="mr-1.5" />
            Download sample sheet
          </Button>

          <label
            className="flex h-40 cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 text-center transition hover:border-primary-400 hover:bg-primary-50/40 dark:border-gray-600 dark:bg-gray-900/40"
            htmlFor="bulk-import-file-input"
          >
            <Upload size={22} className="text-gray-400 dark:text-gray-500" />
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {fileName || "Click to choose a .xlsx, .xls or .csv file"}
            </p>
            <input
              id="bulk-import-file-input"
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={handleFileChange}
            />
          </label>

          {readProgress > 0 && readProgress < 100 && !parseError && (
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>Reading file…</span>
                <span>{readProgress}%</span>
              </div>
              <div className="h-2 w-full rounded-full bg-gray-100 dark:bg-gray-700">
                <div
                  className="h-2 rounded-full bg-primary-500 transition-all"
                  style={{ width: `${readProgress}%` }}
                />
              </div>
            </div>
          )}

          {parseError && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>{parseError}</span>
            </div>
          )}
        </div>
      )}

      {step === "preview" && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Found <strong>{rows.length}</strong> row
              {rows.length === 1 ? "" : "s"} in <strong>{fileName}</strong>. So
              you want to import this?
            </p>
            {invalidRowCount > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 dark:bg-amber-950 dark:text-amber-300">
                <AlertTriangle size={13} />
                {invalidRowCount} row{invalidRowCount === 1 ? "" : "s"} missing
                required fields
              </span>
            )}
          </div>

          <TableV2
            columns={columns.map((col) => ({
              key: col.key,
              header: col.required ? `${col.label} *` : col.label,
              accessor: ({ row }) =>
                row[col.key] === undefined || row[col.key] === "" ? (
                  <span className="text-gray-300 dark:text-gray-600">—</span>
                ) : (
                  String(row[col.key])
                ),
            }))}
            data={previewRows}
            rowClassName={(row) =>
              missingRequiredCount(row) > 0
                ? "bg-red-50/60 dark:bg-red-950/30"
                : ""
            }
          />

          {rows.length > previewRows.length && (
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Showing first {previewRows.length} of {rows.length} rows — the
              rest will also be imported.
            </p>
          )}
        </div>
      )}

      {step === "importing" && (
        <div className="space-y-3 py-6">
          <p className="text-center text-sm text-gray-600 dark:text-gray-300">
            Importing row {Math.min(importProgress + 1, rows.length)} of{" "}
            {rows.length}…
          </p>
          <div className="h-2.5 w-full rounded-full bg-gray-100 dark:bg-gray-700">
            <div
              className="h-2.5 rounded-full bg-primary-500 transition-all"
              style={{
                width: `${rows.length ? Math.round((importProgress / rows.length) * 100) : 0}%`,
              }}
            />
          </div>
          <p className="text-center text-xs text-gray-400 dark:text-gray-500">
            {Math.round((importProgress / (rows.length || 1)) * 100)}%
          </p>
        </div>
      )}

      {step === "result" && result && (
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900 dark:bg-emerald-950">
            <CheckCircle2
              size={20}
              className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400"
            />
            <div>
              <p className="font-medium text-emerald-800 dark:text-emerald-300">
                {result.recordsCreated !== undefined &&
                result.recordsCreated !== result.imported ? (
                  <>
                    Imported {result.imported} of {result.total ?? rows.length}{" "}
                    rows into {result.recordsCreated} {entityLabel}
                  </>
                ) : (
                  <>
                    Imported {result.imported} of {result.total ?? rows.length}{" "}
                    {entityLabel}
                  </>
                )}
              </p>
              {result.failed.length > 0 && (
                <p className="text-sm text-emerald-700 dark:text-emerald-400">
                  {result.failed.length} row{result.failed.length === 1 ? "" : "s"}{" "}
                  couldn't be imported — see details below.
                </p>
              )}
            </div>
          </div>

          {result.failed.length > 0 && (
            <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-xs text-gray-500 dark:bg-gray-900/60 dark:text-gray-400">
                  <tr>
                    <th className="px-3 py-2 font-medium">Row</th>
                    <th className="px-3 py-2 font-medium">Name</th>
                    <th className="px-3 py-2 font-medium">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {result.failed.map((f, idx) => (
                    <tr
                      key={idx}
                      className="border-t border-gray-100 dark:border-gray-700"
                    >
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                        {f.index >= 0 ? f.index + 2 : "-"}
                      </td>
                      <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                        {f.name || "—"}
                      </td>
                      <td className="px-3 py-2 text-red-600 dark:text-red-400">
                        {f.error}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default BulkImportModal;
